/**
 * 连接创建菜单：从锚点拖线到空白处时弹出，选择创建新节点类型。
 *
 * 使用 createPortal 渲染到 document.body。
 */
import { useRef } from 'react';
import { createPortal } from 'react-dom';
import { Type, Wand2, Clapperboard, MessageSquare, Bot } from 'lucide-react';
import type { CanvasNodeType } from './types';
import { boardActions, getBoardState } from './state/boardStore';
import { makeNode, nid } from './utils';
import { useMenuExternalDismiss } from './hooks/useMenuExternalDismiss';
import toast from '../../components/Toast';
import { defaultNodeData } from './NodeCreateMenu';
import {
  canConnectNodeTypes,
  getAllowedTargetTypes,
  getInvalidConnectionMessage,
} from './connectionRules';

interface ConnectionCreateMenuProps {
  x: number;
  y: number;
  sourceNodeId: string;
  onClose: () => void;
}

// 仅列「消费输入」的节点类型：图片节点 / 图片组是结果容器（内容由生成、
// 抽帧、聚合等系统操作产出），"创建并连接"建出的只会是空壳，故不提供。
const MENU_GROUPS: { label: string; items: { type: CanvasNodeType; label: string; icon: React.ReactNode; color: string }[] }[] = [
  {
    label: '文本',
    items: [
      { type: 'text', label: '文本节点', icon: <Type className="h-4 w-4" />, color: 'text-slate-600' },
      { type: 'llm', label: 'LLM 节点', icon: <MessageSquare className="h-4 w-4" />, color: 'text-amber-600' },
    ],
  },
  {
    label: '图片',
    items: [
      { type: 'generate_image', label: '生图配置', icon: <Wand2 className="h-4 w-4" />, color: 'text-blue-600' },
      { type: 'agent', label: '智能体', icon: <Bot className="h-4 w-4" />, color: 'text-indigo-600' },
    ],
  },
  {
    label: '视频',
    items: [
      { type: 'generate_video', label: '生视频配置', icon: <Clapperboard className="h-4 w-4" />, color: 'text-purple-600' },
    ],
  },
];

export function ConnectionCreateMenu({ x, y, sourceNodeId, onClose }: ConnectionCreateMenuProps) {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const cur = getBoardState();
  const sourceNode = cur.nodes.find((n) => n.id === sourceNodeId);
  const allowedTargets = sourceNode ? getAllowedTargetTypes(sourceNode.type) : [];
  const filterItem = (item: { type: CanvasNodeType }) => {
    if (!allowedTargets.includes(item.type)) return false;
    // 新建的生视频节点没有模型，必然过不了"参考视频/参考音频能力"节点级判定，
    // 从视频节点/声音节点拖到空白不提供该选项（先建节点选好支持的模型再连线）
    if (sourceNode?.type === 'video' && item.type === 'generate_video') return false;
    if (sourceNode?.type === 'audio' && item.type === 'generate_video') return false;
    return true;
  };
  const visibleGroups = MENU_GROUPS.map((g) => ({
    ...g,
    items: g.items.filter(filterItem),
  })).filter((g) => g.items.length > 0);
  const totalVisible = visibleGroups.reduce((sum, g) => sum + g.items.length, 0);

  // 点击外部 / Escape / 画布缩放时关闭（见 useMenuExternalDismiss 注释）
  useMenuExternalDismiss(menuRef, onClose);

  const handleSelect = (type: CanvasNodeType) => {
    const currentState = getBoardState();
    const currentSourceNode = currentState.nodes.find((n) => n.id === sourceNodeId);
    if (!currentSourceNode) {
      onClose();
      return;
    }
    if (!canConnectNodeTypes(currentSourceNode.type, type)) {
      toast.error(getInvalidConnectionMessage(currentSourceNode.type, type));
      onClose();
      return;
    }

    // 在释放位置创建新节点（将屏幕坐标转为画布坐标）
    const container = document.querySelector('[data-canvas-bg]');
    if (!container) {
      onClose();
      return;
    }
    const rect = container.getBoundingClientRect();
    const canvasX = (x - rect.left - currentState.viewport.x) / currentState.viewport.zoom;
    const canvasY = (y - rect.top - currentState.viewport.y) / currentState.viewport.zoom;

    // 根据节点类型创建默认 data（与 NodeCreateMenu 共用同一份定义）
    const newNode = makeNode(type, { x: canvasX, y: canvasY }, defaultNodeData(type));
    boardActions.addNode(newNode);
    boardActions.addEdge({
      id: nid(),
      sourceNodeId,
      targetNodeId: newNode.id,
      sourceHandle: 'right',
      targetHandle: 'left',
    });
    onClose();
  };

  // 确保菜单不超出视口
  const menuWidth = 180;
  const menuHeight = 200;
  const posX = Math.min(x, window.innerWidth - menuWidth - 8);
  const posY = Math.min(y, window.innerHeight - menuHeight - 8);

  return createPortal(
    <div
      ref={menuRef}
      className="fixed z-[10001] min-w-[180px] overflow-hidden rounded-xl border border-slate-200 bg-white py-1.5 shadow-xl shadow-slate-950/10"
      style={{ left: posX, top: posY }}
    >
      <div className="px-3 py-1.5 text-xs font-medium text-slate-400">创建并连接</div>
      {visibleGroups.map((group, gi) => (
        <div key={group.label}>
          {gi > 0 && <div className="my-1 h-px bg-slate-100" />}
          <div className="px-3 py-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            {group.label}
          </div>
          {group.items.map((item) => (
            <button
              key={item.type}
              type="button"
              onClick={() => handleSelect(item.type)}
              className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-slate-700 transition hover:bg-slate-50"
            >
              <span className={item.color}>{item.icon}</span>
              <span>{item.label}</span>
            </button>
          ))}
        </div>
      ))}
      {totalVisible === 0 && (
        <div className="px-3 py-2 text-xs text-slate-400">当前节点没有可连接的新节点类型</div>
      )}
    </div>,
    document.body
  );
}
